
import React, { useState, useEffect } from 'react';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format, addMonths } from 'date-fns';
import { pl } from 'date-fns/locale';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { formSchema, getYearsList } from '@/utils/contactFormUtils';
import { useIsMobile } from '@/hooks/use-mobile';

interface WeddingDatePickerProps {
  form: UseFormReturn<z.infer<typeof formSchema>>;
}

const WeddingDatePicker: React.FC<WeddingDatePickerProps> = ({ form }) => {
  const isMobile = useIsMobile();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const years = getYearsList();
  const today = new Date();
  const defaultDate = addMonths(today, 1);
  
  // State to control the calendar's current month view
  const [currentMonth, setCurrentMonth] = useState<Date>(form.getValues().wedding_date || defaultDate);
  
  // Set default date if not selected
  useEffect(() => {
    if (!form.getValues().wedding_date) {
      form.setValue('wedding_date', defaultDate);
      setCurrentMonth(defaultDate);
    }
  }, [form, defaultDate]);

  // Function to update year
  const handleYearChange = (year: string) => {
    const currentDate = form.getValues().wedding_date || new Date();
    
    // Create new date with selected year
    const newDate = new Date(currentDate);
    newDate.setFullYear(parseInt(year));
    
    // Update the form value
    form.setValue('wedding_date', newDate);
    
    // Also update the calendar display month to match the new year
    const newCalendarMonth = new Date(currentMonth);
    newCalendarMonth.setFullYear(parseInt(year));
    setCurrentMonth(newCalendarMonth);
  };

  return (
    <FormField
      control={form.control}
      name="wedding_date"
      render={({ field }) => (
        <FormItem className="space-y-1">
          <FormLabel className={`text-${isMobile ? 'xs' : 'sm'} font-inter text-left block text-elegant-700 tracking-wider font-light uppercase`}>DATA ŚLUBU</FormLabel>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant="outline"
                  className={`w-full flex items-center justify-between pl-3 ${isMobile ? 'py-1.5 text-sm' : 'py-5'} text-left bg-white border-elegant-200 text-elegant-900 hover:bg-elegant-100 hover:text-elegant-800 shadow-inner shadow-elegant-100/20`}
                  onClick={() => setCalendarOpen(true)}
                  type="button"
                >
                  <div className="flex items-center">
                    <CalendarIcon className="mr-2 h-4 w-4 opacity-70" />
                    {field.value ? (
                      format(field.value, "dd MMMM yyyy", { locale: pl })
                    ) : (
                      <span className="text-elegant-400">Wybierz datę ślubu</span>
                    )}
                  </div>
                  <span className="opacity-50">▼</span>
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent 
              className={`${isMobile ? 'w-screen max-w-[calc(100vw-32px)]' : 'w-auto'} p-0 bg-white border-elegant-200 shadow-xl z-50`} 
              align="start"
            >
              <div className="p-3 bg-white">
                <Select
                  value={field.value ? field.value.getFullYear().toString() : new Date().getFullYear().toString()}
                  onValueChange={handleYearChange}
                >
                  <SelectTrigger className={`w-full mb-2 ${isMobile ? 'text-sm' : ''}`}>
                    <SelectValue placeholder="Wybierz rok" />
                  </SelectTrigger>
                  <SelectContent position="popper" className="bg-white max-h-[200px] overflow-y-auto">
                    {years.map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Calendar
                  mode="single"
                  selected={field.value}
                  onSelect={(date) => {
                    if (date) {
                      field.onChange(date);
                      // Close calendar after selection
                      setTimeout(() => setCalendarOpen(false), 300);
                    }
                  }}
                  disabled={(date) => date < today}
                  initialFocus
                  locale={pl}
                  month={currentMonth}
                  onMonthChange={setCurrentMonth}
                  className="pointer-events-auto"
                />
              </div>
            </PopoverContent>
          </Popover>
          <FormMessage className="text-red-500 text-xs" />
        </FormItem>
      )}
    />
  );
};

export default WeddingDatePicker;
